import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { extractNestedAnswers } from "@/encounters/components/PostpartumEncounter/utils/postpartum";
import {
  NewbornPostpartumEncounterQuestionnaireURLs,
  NewbornPostpartumInputsOutputLabels,
  NewbornPostpartumInputsOutputsQuestionsLinkIds,
} from "@/encounters/constants/newborn-postpartum";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";

const InputsAndOutputsDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          NewbornPostpartumEncounterQuestionnaireURLs.InputsOutputs,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no inputs/outputs data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Inputs & Outputs</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No inputs & outputs data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If inputs/outputs data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Extract values for all inputs/outputs fields using extractNestedAnswers
  const previousAnswers = extractNestedAnswers(effectivePreviousVersion);
  const currentAnswers = extractNestedAnswers(currentVersion);

  // Basic feeding fields
  const previousFeedingType =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingType]?.[0] || "";
  const currentFeedingType =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingType]?.[0] || "";

  const previousFeedingLength =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingLength]?.[0] || "";
  const currentFeedingLength =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingLength]?.[0] || "";

  const previousFeedingBehavior =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingBehavior] || [];
  const currentFeedingBehavior =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingBehavior] || [];

  const previousFeedingSupplementAmount =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmount]?.[0] ||
    "";
  const currentFeedingSupplementAmount =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmount]?.[0] ||
    "";

  const previousFeedingSupplement =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplement] || [];
  const currentFeedingSupplement =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplement] || [];

  // Basic supplement amounts
  const previousPumpedMilkAmount =
    previousAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountPumpedMilk
    ]?.[0] || "";
  const currentPumpedMilkAmount =
    currentAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountPumpedMilk
    ]?.[0] || "";

  const previousPumpedMilkUnit =
    effectivePreviousVersion.item?.find(
      (item) =>
        item.linkId ===
        NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitPumpedMilk,
    )?.answer?.[0]?.valueCoding?.display || "";
  const currentPumpedMilkUnit =
    currentVersion.item?.find(
      (item) =>
        item.linkId ===
        NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitPumpedMilk,
    )?.answer?.[0]?.valueCoding?.display || "";

  const previousPumpedMilkAverageAmount =
    previousAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountPumpedMilk
    ]?.[0] || "";
  const currentPumpedMilkAverageAmount =
    currentAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountPumpedMilk
    ]?.[0] || "";

  const previousDonorMilkAmount =
    previousAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountDonorMilk
    ]?.[0] || "";
  const currentDonorMilkAmount =
    currentAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountDonorMilk
    ]?.[0] || "";

  const previousDonorMilkUnit =
    effectivePreviousVersion.item?.find(
      (item) =>
        item.linkId ===
        NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitDonorMilk,
    )?.answer?.[0]?.valueCoding?.display || "";
  const currentDonorMilkUnit =
    currentVersion.item?.find(
      (item) =>
        item.linkId ===
        NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitDonorMilk,
    )?.answer?.[0]?.valueCoding?.display || "";

  const previousDonorMilkAverageAmount =
    previousAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountDonorMilk
    ]?.[0] || "";
  const currentDonorMilkAverageAmount =
    currentAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountDonorMilk
    ]?.[0] || "";

  const previousFormulaAmount =
    previousAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountFormula
    ]?.[0] || "";
  const currentFormulaAmount =
    currentAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountFormula
    ]?.[0] || "";

  const previousFormulaUnit =
    effectivePreviousVersion.item?.find(
      (item) =>
        item.linkId ===
        NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitFormula,
    )?.answer?.[0]?.valueCoding?.display || "";
  const currentFormulaUnit =
    currentVersion.item?.find(
      (item) =>
        item.linkId ===
        NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitFormula,
    )?.answer?.[0]?.valueCoding?.display || "";

  const previousFormulaAverageAmount =
    previousAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountFormula
    ]?.[0] || "";
  const currentFormulaAverageAmount =
    currentAnswers[
      NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountFormula
    ]?.[0] || "";

  // Voiding fields
  const previousVoiding =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.Voiding] || [];
  const currentVoiding =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.Voiding] || [];

  const previousVoidingNormal =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.VoidingNormal]?.[0] || "";
  const currentVoidingNormal =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.VoidingNormal]?.[0] || "";

  const previousVoidingNotNormal =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.VoidingNotNormal]?.[0] || "";
  const currentVoidingNotNormal =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.VoidingNotNormal]?.[0] || "";

  // Bowel fields
  const previousBowel = previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.Bowel] || [];
  const currentBowel = currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.Bowel] || [];

  const previousBowelNormal =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.BowelNormal]?.[0] || "";
  const currentBowelNormal =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.BowelNormal]?.[0] || "";

  const previousBowelNotNormal =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.BowelNotNormal]?.[0] || "";
  const currentBowelNotNormal =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.BowelNotNormal]?.[0] || "";

  // Notes
  const previousNotes =
    previousAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.Notes]?.[0] || "";
  const currentNotes =
    currentAnswers[NewbornPostpartumInputsOutputsQuestionsLinkIds.Notes]?.[0] || "";

  // Check if both versions are completely empty
  const isCompletelyEmpty =
    !previousFeedingType &&
    !previousFeedingLength &&
    !previousFeedingBehavior.length &&
    !previousFeedingSupplementAmount &&
    !previousFeedingSupplement.length &&
    !previousPumpedMilkAmount &&
    !previousPumpedMilkUnit &&
    !previousDonorMilkAmount &&
    !previousDonorMilkUnit &&
    !previousFormulaAmount &&
    !previousPumpedMilkUnit &&
    !previousVoiding.length &&
    !previousVoidingNormal &&
    !previousVoidingNotNormal &&
    !previousBowel.length &&
    !previousBowelNormal &&
    !previousBowelNotNormal &&
    !previousNotes &&
    !currentFeedingType &&
    !currentFeedingLength &&
    !currentFeedingBehavior.length &&
    !currentFeedingSupplementAmount &&
    !currentFeedingSupplement.length &&
    !currentPumpedMilkAmount &&
    !currentPumpedMilkUnit &&
    !currentDonorMilkAmount &&
    !currentDonorMilkUnit &&
    !currentFormulaAmount &&
    !currentFormulaUnit &&
    !currentVoiding.length &&
    !currentVoidingNormal &&
    !currentVoidingNotNormal &&
    !currentBowel.length &&
    !currentBowelNormal &&
    !currentBowelNotNormal &&
    !currentNotes;

  if (isCompletelyEmpty) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Inputs & Outputs</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No inputs & outputs data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Inputs & Outputs</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              {previousFeedingType && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FeedingType}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentFeedingType}
                    previous={previousFeedingType}
                  />
                </Stack>
              )}
              {previousFeedingLength && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FeedingLength}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentFeedingLength}
                    previous={previousFeedingLength}
                  />
                </Stack>
              )}
              {previousFeedingBehavior.length > 0 && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FeedingBehavior}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentFeedingBehavior.join(", ")}
                    previous={previousFeedingBehavior.join(", ")}
                  />
                </Stack>
              )}
              {previousFeedingSupplementAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.BreastChestFeedingsAmount}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentFeedingSupplementAmount}
                    previous={previousFeedingSupplementAmount}
                  />
                </Stack>
              )}
              {previousFeedingSupplement.length > 0 && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.SupplementType}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentFeedingSupplement.join(", ")}
                    previous={previousFeedingSupplement.join(", ")}
                  />
                </Stack>
              )}
              {previousPumpedMilkAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.PumpedMilkNumberFeedings}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentPumpedMilkAmount}
                    previous={previousPumpedMilkAmount}
                  />
                </Stack>
              )}
              {previousPumpedMilkUnit && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.PumpedMilkAmountUnit}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentPumpedMilkUnit}
                    previous={previousPumpedMilkUnit}
                  />
                </Stack>
              )}
              {previousPumpedMilkAverageAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.PumpedMilkAverageAmount}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentPumpedMilkAverageAmount}
                    previous={previousPumpedMilkAverageAmount}
                  />
                </Stack>
              )}
              {previousDonorMilkAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.DonorMilkNumberFeedings}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentDonorMilkAmount}
                    previous={previousDonorMilkAmount}
                  />
                </Stack>
              )}
              {previousDonorMilkUnit && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.DonorMilkAmountUnit}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentDonorMilkUnit}
                    previous={previousDonorMilkUnit}
                  />
                </Stack>
              )}
              {previousDonorMilkAverageAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.DonorMilkAverageAmount}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentDonorMilkAverageAmount}
                    previous={previousDonorMilkAverageAmount}
                  />
                </Stack>
              )}
              {previousFormulaAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FormulaNumberFeedings}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentFormulaAmount}
                    previous={previousFormulaAmount}
                  />
                </Stack>
              )}
              {previousFormulaUnit && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FormulaAmountUnit}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentFormulaUnit}
                    previous={previousFormulaUnit}
                  />
                </Stack>
              )}
              {previousFormulaAverageAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FormulaAverageAmount}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentFormulaAverageAmount}
                    previous={previousFormulaAverageAmount}
                  />
                </Stack>
              )}
              {previousVoiding.length > 0 && (
                <Stack gap="0">
                  <TextWrapper fw={600}>{NewbornPostpartumInputsOutputLabels.Voiding}</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentVoiding.join(", ")}
                    previous={previousVoiding.join(", ")}
                  />
                </Stack>
              )}
              {previousVoidingNormal && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.VoidingNormalNotes}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentVoidingNormal}
                    previous={previousVoidingNormal}
                  />
                </Stack>
              )}
              {previousVoidingNotNormal && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.VoidingNotNormalNotes}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentVoidingNotNormal}
                    previous={previousVoidingNotNormal}
                  />
                </Stack>
              )}
              {previousBowel.length > 0 && (
                <Stack gap="0">
                  <TextWrapper fw={600}>{NewbornPostpartumInputsOutputLabels.Bowel}</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentBowel.join(", ")}
                    previous={previousBowel.join(", ")}
                  />
                </Stack>
              )}
              {previousBowelNormal && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.BowelNormalNotes}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentBowelNormal}
                    previous={previousBowelNormal}
                  />
                </Stack>
              )}
              {previousBowelNotNormal && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.BowelNotNormalNotes}
                  </TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentBowelNotNormal}
                    previous={previousBowelNotNormal}
                  />
                </Stack>
              )}
              {previousNotes && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.InputsOutputsNotes}
                  </TextWrapper>
                  <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              {currentFeedingType && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FeedingType}
                  </TextWrapper>
                  <WordsDiff current={currentFeedingType} previous={previousFeedingType} />
                </Stack>
              )}
              {currentFeedingLength && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FeedingLength}
                  </TextWrapper>
                  <WordsDiff current={currentFeedingLength} previous={previousFeedingLength} />
                </Stack>
              )}
              {currentFeedingBehavior.length > 0 && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FeedingBehavior}
                  </TextWrapper>
                  <WordsDiff
                    current={currentFeedingBehavior.join(", ")}
                    previous={previousFeedingBehavior.join(", ")}
                  />
                </Stack>
              )}
              {currentFeedingSupplementAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.BreastChestFeedingsAmount}
                  </TextWrapper>
                  <WordsDiff
                    current={currentFeedingSupplementAmount}
                    previous={previousFeedingSupplementAmount}
                  />
                </Stack>
              )}
              {currentFeedingSupplement.length > 0 && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.SupplementType}
                  </TextWrapper>
                  <WordsDiff
                    current={currentFeedingSupplement.join(", ")}
                    previous={previousFeedingSupplement.join(", ")}
                  />
                </Stack>
              )}
              {currentPumpedMilkAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.PumpedMilkNumberFeedings}
                  </TextWrapper>
                  <WordsDiff
                    current={currentPumpedMilkAmount}
                    previous={previousPumpedMilkAmount}
                  />
                </Stack>
              )}
              {currentPumpedMilkUnit && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.PumpedMilkAmountUnit}
                  </TextWrapper>
                  <WordsDiff current={currentPumpedMilkUnit} previous={previousPumpedMilkUnit} />
                </Stack>
              )}
              {currentPumpedMilkAverageAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.PumpedMilkAverageAmount}
                  </TextWrapper>
                  <WordsDiff
                    current={currentPumpedMilkAverageAmount}
                    previous={previousPumpedMilkAverageAmount}
                  />
                </Stack>
              )}
              {currentDonorMilkAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.DonorMilkNumberFeedings}
                  </TextWrapper>
                  <WordsDiff current={currentDonorMilkAmount} previous={previousDonorMilkAmount} />
                </Stack>
              )}
              {currentDonorMilkUnit && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.DonorMilkAmountUnit}
                  </TextWrapper>
                  <WordsDiff current={currentDonorMilkUnit} previous={previousDonorMilkUnit} />
                </Stack>
              )}
              {currentDonorMilkAverageAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.DonorMilkAverageAmount}
                  </TextWrapper>
                  <WordsDiff
                    current={currentDonorMilkAverageAmount}
                    previous={previousDonorMilkAverageAmount}
                  />
                </Stack>
              )}
              {currentFormulaAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FormulaNumberFeedings}
                  </TextWrapper>
                  <WordsDiff current={currentFormulaAmount} previous={previousFormulaAmount} />
                </Stack>
              )}
              {currentFormulaUnit && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FormulaAmountUnit}
                  </TextWrapper>
                  <WordsDiff current={currentFormulaUnit} previous={previousFormulaUnit} />
                </Stack>
              )}
              {currentFormulaAverageAmount && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.FormulaAverageAmount}
                  </TextWrapper>
                  <WordsDiff
                    current={currentFormulaAverageAmount}
                    previous={previousFormulaAverageAmount}
                  />
                </Stack>
              )}
              {currentVoiding.length > 0 && (
                <Stack gap="0">
                  <TextWrapper fw={600}>{NewbornPostpartumInputsOutputLabels.Voiding}</TextWrapper>
                  <WordsDiff
                    current={currentVoiding.join(", ")}
                    previous={previousVoiding.join(", ")}
                  />
                </Stack>
              )}
              {currentVoidingNormal && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.VoidingNormalNotes}
                  </TextWrapper>
                  <WordsDiff current={currentVoidingNormal} previous={previousVoidingNormal} />
                </Stack>
              )}
              {currentVoidingNotNormal && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.VoidingNotNormalNotes}
                  </TextWrapper>
                  <WordsDiff
                    current={currentVoidingNotNormal}
                    previous={previousVoidingNotNormal}
                  />
                </Stack>
              )}
              {currentBowel.length > 0 && (
                <Stack gap="0">
                  <TextWrapper fw={600}>{NewbornPostpartumInputsOutputLabels.Bowel}</TextWrapper>
                  <WordsDiff
                    current={currentBowel.join(", ")}
                    previous={previousBowel.join(", ")}
                  />
                </Stack>
              )}
              {currentBowelNormal && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.BowelNormalNotes}
                  </TextWrapper>
                  <WordsDiff current={currentBowelNormal} previous={previousBowelNormal} />
                </Stack>
              )}
              {currentBowelNotNormal && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.BowelNotNormalNotes}
                  </TextWrapper>
                  <WordsDiff current={currentBowelNotNormal} previous={previousBowelNotNormal} />
                </Stack>
              )}
              {currentNotes && (
                <Stack gap="0">
                  <TextWrapper fw={600}>
                    {NewbornPostpartumInputsOutputLabels.InputsOutputsNotes}
                  </TextWrapper>
                  <WordsDiff current={currentNotes} previous={previousNotes} />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default InputsAndOutputsDiff;
